/**
 * RTWO Event Bus — Internal Event Bus
 */

export const EBusInternalEventTypes = {
  EVENT_REGISTERED: 'event_registered',
  EVENT_PUBLISHED: 'event_published',
  EVENT_ROUTED: 'event_routed',
  EVENT_DELIVERED: 'event_delivered',
  EVENT_FAILED: 'event_failed',
  PUBLISHER_REGISTERED: 'publisher_registered',
  SUBSCRIBER_REGISTERED: 'subscriber_registered',
  ROUTE_REGISTERED: 'route_registered',
  DEAD_LETTER_ADDED: 'dead_letter_added',
} as const;

export type EBusInternalEventType =
  (typeof EBusInternalEventTypes)[keyof typeof EBusInternalEventTypes];

export interface EBusInternalEvent<T = unknown> {
  id: string;
  type: EBusInternalEventType;
  moduleId: 'RTWO-EBUS-001';
  payload: T;
  timestamp: number | null;
}

export type EBusInternalEventHandler<T = unknown> = (event: EBusInternalEvent<T>) => void;

class EBusInternalEventBus {
  private subscriptions = new Map<EBusInternalEventType, EBusInternalEventHandler[]>();

  subscribe<T>(eventType: EBusInternalEventType, handler: EBusInternalEventHandler<T>): void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    handlers.push(handler as EBusInternalEventHandler);
    this.subscriptions.set(eventType, handlers);
  }

  unsubscribe<T>(eventType: EBusInternalEventType, handler: EBusInternalEventHandler<T>): void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    this.subscriptions.set(
      eventType,
      handlers.filter((entry) => entry !== handler)
    );
  }

  emit<T>(eventType: EBusInternalEventType, payload: T): string {
    const event: EBusInternalEvent<T> = {
      id: `ebus_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type: eventType,
      moduleId: 'RTWO-EBUS-001',
      payload,
      timestamp: null,
    };

    const handlers = this.subscriptions.get(eventType) ?? [];
    for (const handler of handlers) {
      try {
        handler(event);
      } catch (error) {
        console.error(`[EBusInternalEventBus] Handler error for ${eventType}:`, error);
      }
    }

    return event.id;
  }
}

export const ebusInternalEventBus = new EBusInternalEventBus();
